import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight, FileDown,
  Network, ShieldCheck, Cpu, Sparkles, TrendingUp, ArrowRight, Zap,
  Camera, Play, BookOpen, Download,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.13av — Industrial Connector LIVE DEMO.
//
// Distinct from /industrial/connector (the cold-outreach pilot landing
// page) — this URL is a standalone, no-form, no-CTA-required artefact for
// investor calls and mining-superintendent screenshares. Streams
// realistic-looking telemetry from a simulated tier-2 WA iron-ore site
// (entirely client-side · no network calls · no real data).
//
// Designed to be the demo you drop into a Loom or share over Zoom while
// you walk through the connector architecture story. The page tells the
// "read-only · no PLC touch · live in 4 weeks" story by SHOWING the
// engine running, not by promising it.

// ─── Site profiles · Phase 25.13ax ──────────────────────────────────────
// Three industry profiles the operator can switch between live during a
// screenshare. Each carries its own tag taxonomy, baseline values, and
// auto-brief writer so the engine looks credibly purpose-built for each
// audience (iron ore · water utility · underground gold).
//
// Profile-aware brief writers know how to call out cavitation, leak
// signatures, mass-balance gaps, dewatering pump degradation — not just
// generic % deviations. This is what the production DARA Engine actually
// does for real sites.
const SITE_PROFILES = {
  ironore: {
    id: "ironore",
    name: "Pit 4 · Tier-2 WA Iron Ore",
    short: "Iron ore",
    tags: [
      { id: "BELT-7B-SCALE",      label: "Belt 7B · ROM → crusher",       unit: "tph",   baseline: 4_820,  range: 320,  source: "OSIsoft PI" },
      { id: "SHOVEL-442-CYCLE",   label: "Shovel 442 · cycle time",       unit: "s",     baseline: 28.4,   range: 1.6,  source: "Wenco FMS" },
      { id: "TRUCK-612-PAYLOAD",  label: "Truck 612 · payload",           unit: "t",     baseline: 354,    range: 14,   source: "Cat VIMS" },
      { id: "PIT4-ROM-RATE",      label: "Pit 4 · ROM stockpile delta",   unit: "tph",   baseline: 4_790,  range: 240,  source: "Aveva PI" },
      { id: "TRAIN-LOADOUT",      label: "Train load-out · wagon avg",    unit: "t",     baseline: 116.2,  range: 1.8,  source: "Siemens S7" },
      { id: "CRUSHER-2-AMPS",     label: "Crusher 2 · motor amps",        unit: "A",     baseline: 412,    range: 22,   source: "Modbus TCP" },
      { id: "PORT-STOCKPILE",     label: "Port stockpile (Hedland)",      unit: "kt",    baseline: 184.6,  range: 0.6,  source: "Port operator" },
    ],
  },
  water: {
    id: "water",
    name: "Northam Booster · Water Utility · WA",
    short: "Water utility",
    tags: [
      { id: "B2-DISCH-PRESS",     label: "Booster 2 · discharge press",   unit: "kPa",   baseline: 624,    range: 14,   source: "Aveva PI" },
      { id: "B2-SUCT-PRESS",      label: "Booster 2 · suction press",     unit: "kPa",   baseline: 142,    range: 6,    source: "Aveva PI" },
      { id: "B2-MOTOR-AMPS",      label: "Booster 2 · motor amps",        unit: "A",     baseline: 86,     range: 3.5,  source: "Modbus TCP" },
      { id: "B2-VIBRATION",       label: "Booster 2 · axial vibration",   unit: "mm/s",  baseline: 2.4,    range: 0.4,  source: "Hilscher gw" },
      { id: "B2-BEARING-TEMP",    label: "Booster 2 · bearing temp",      unit: "°C",    baseline: 58,     range: 1.8,  source: "OPC UA" },
      { id: "RM-FLOW-DELIVERY",   label: "Rising main · flow delivery",   unit: "L/s",   baseline: 412,    range: 12,   source: "ABB AquaMaster" },
      { id: "RES7-LEVEL",         label: "Reservoir 7 · level",           unit: "m",     baseline: 8.4,    range: 0.15, source: "Siemens S7" },
      // Phase 25.14c — chemical-dose drift + blower energy waste tags so the
      // simulator demonstrates the full set of capabilities advertised on
      // /industries/water (chemical dosing, blower energy, throughput).
      { id: "CHLOR-RES-MGL",      label: "Chlorine residual · post-dose", unit: "mg/L",  baseline: 1.20,   range: 0.06, source: "Siemens SIPROCESS" },
      { id: "BLOWER-1-AMPS",      label: "Aeration blower 1 · motor amps", unit: "A",    baseline: 162,    range: 4.5,  source: "OPC UA" },
    ],
  },
  gold: {
    id: "gold",
    name: "Underground Gold Mine · 850 RL · WA",
    short: "Underground gold",
    tags: [
      { id: "DEWATER-P3-AMPS",    label: "Dewatering pump 3 · amps",      unit: "A",     baseline: 248,    range: 9,    source: "OSIsoft PI" },
      { id: "DEWATER-P3-VIB",     label: "Dewatering pump 3 · vibration", unit: "mm/s",  baseline: 3.1,    range: 0.4,  source: "Hilscher gw" },
      { id: "VENT-FAN-2-AMPS",    label: "Vent fan 2 · motor amps",       unit: "A",     baseline: 312,    range: 8,    source: "Modbus TCP" },
      { id: "CIL-TANK-3-PH",      label: "CIL tank 3 · pH",               unit: "pH",    baseline: 10.6,   range: 0.12, source: "Aveva PI" },
      { id: "MILL-1-MOTOR-AMPS",  label: "SAG mill · motor amps",         unit: "A",     baseline: 1_840,  range: 42,   source: "OPC UA" },
      { id: "MILL-FEED-RATE",     label: "Mill feed conveyor",            unit: "tph",   baseline: 408,    range: 22,   source: "Siemens S7" },
      { id: "TAILINGS-PUMP-PRES", label: "Tailings pump · discharge",     unit: "kPa",   baseline: 1_840,  range: 32,   source: "Modbus TCP" },
    ],
  },
  // ── Phase 25.16f — Ventia 7-sector coverage profiles ──────────────
  // Five additional profiles that, combined with the existing iron-ore
  // (= "Energy & Resources") and water profiles, close the entire
  // Ventia 7-sector mapping with Australian-realistic site names,
  // tag taxonomies, and failure-mode narratives. Used both inside this
  // standalone demo (profile-switcher) AND inside /ventia/demo (the
  // 7-tile dashboard that runs all sectors simultaneously).
  transport: {
    id: "transport",
    name: "M5 East Tunnel · Sydney · Eastbound · TfNSW asset",
    short: "Transport · Tunnels",
    sector: "01",
    sector_label: "Transport · Roads · Tunnels",
    tags: [
      { id: "JET-FAN-3-AMPS",      label: "Jet fan 3 · motor amps",          unit: "A",   baseline: 84,    range: 3,    source: "Modbus TCP" },
      { id: "JET-FAN-7-AMPS",      label: "Jet fan 7 · motor amps",          unit: "A",   baseline: 86,    range: 3,    source: "Modbus TCP" },
      { id: "CO-EAST-PORTAL",      label: "CO · east portal",                unit: "ppm", baseline: 9.4,   range: 1.2,  source: "Modbus TCP" },
      { id: "NOX-MIDPOINT",        label: "NOx · midpoint",                  unit: "ppm", baseline: 0.32,  range: 0.06, source: "OPC UA" },
      { id: "VMS-SIGN-12-LATENCY", label: "VMS sign 12 · heartbeat latency", unit: "ms",  baseline: 240,   range: 30,   source: "SNMP" },
      { id: "CCTV-PTZ-7-UPTIME",   label: "CCTV PTZ 7 · 30-min uptime",      unit: "%",   baseline: 99.6,  range: 0.3,  source: "Genetec VMS" },
      { id: "LIGHTING-CKT-3-LOAD", label: "Lighting circuit 3 · load",       unit: "kW",  baseline: 42,    range: 2,    source: "Modbus TCP" },
    ],
  },
  telco: {
    id: "telco",
    name: "POI 412 · NBN · Sydney North · Carrier-grade",
    short: "Telecommunications",
    sector: "02",
    sector_label: "Telecommunications · Cross-carrier",
    tags: [
      { id: "BACKHAUL-LINK-A-UTIL", label: "Backhaul link A · utilisation",  unit: "%",   baseline: 68,    range: 5,    source: "SNMP · Cisco" },
      { id: "BACKHAUL-LINK-B-UTIL", label: "Backhaul link B · utilisation",  unit: "%",   baseline: 71,    range: 4,    source: "SNMP · Cisco" },
      { id: "ALARM-RATE-15M",       label: "Alarm rate · 15-min window",     unit: "ev",  baseline: 12,    range: 3,    source: "Cisco Prime" },
      { id: "OPT-RX-NODE-47",       label: "Optical RX power · Node 47",     unit: "dBm", baseline: 7.2,   range: 0.4,  source: "Nokia NMS" },
      { id: "BTS-HO-FAIL-15M",      label: "Failed handovers · 15-min",      unit: "ev",  baseline: 8,     range: 2,    source: "Ericsson OSS" },
      { id: "TOWER-AC-AMPS",        label: "Tower AC current draw",          unit: "A",   baseline: 36,    range: 1.2,  source: "Modbus TCP" },
      { id: "BATT-STRING-V",        label: "Battery backup string voltage",  unit: "V",   baseline: 53.6,  range: 0.3,  source: "SNMP" },
    ],
  },
  defence: {
    id: "defence",
    name: "RAAF Base Pearce · Base BMS · WA · Non-classified estate",
    short: "Defence · Base estate",
    sector: "05",
    sector_label: "Defence · Base Infrastructure (non-classified)",
    tags: [
      { id: "CHILLER-1-COP",       label: "Chiller 1 · COP",                 unit: "",    baseline: 4.6,   range: 0.25, source: "Tridium Niagara" },
      { id: "CHILLER-2-COP",       label: "Chiller 2 · COP",                 unit: "",    baseline: 4.5,   range: 0.25, source: "Tridium Niagara" },
      { id: "RING-MAIN-PRESSURE",  label: "Base ring-main pressure",         unit: "kPa", baseline: 520,   range: 10,   source: "Modbus TCP" },
      { id: "RTU-12-FAN-AMPS",     label: "RTU 12 · supply fan amps",        unit: "A",   baseline: 22,    range: 1.0,  source: "Honeywell EBI" },
      { id: "FEEDER-A-LOAD",       label: "Feeder A · base electrical load", unit: "kW",  baseline: 1_820, range: 55,   source: "SCADA" },
      { id: "GENERATOR-1-FUEL",    label: "Generator 1 · diesel level",      unit: "%",   baseline: 88,    range: 0.8,  source: "Modbus TCP" },
      { id: "MESS-HW-RETURN-TEMP", label: "Mess hall · HW return temp",      unit: "°C",  baseline: 64,    range: 1.2,  source: "BACnet" },
    ],
  },
  social: {
    id: "social",
    name: "Royal Prince Alfred · Sydney · Tridium Niagara BMS",
    short: "Hospital · Social Infra",
    sector: "06",
    sector_label: "Social Infrastructure · Hospitals · Public Estate",
    tags: [
      { id: "CHILLER-PLANT-COP",    label: "Chilled-water plant · COP",       unit: "",    baseline: 4.8,   range: 0.25, source: "Tridium Niagara" },
      { id: "AHU-12-SUPPLY-TEMP",   label: "AHU 12 · supply temp",            unit: "°C",  baseline: 13.2,  range: 0.5,  source: "Honeywell EBI" },
      { id: "ICU-AHU-DELTA-P",      label: "ICU AHU · differential pressure", unit: "Pa",  baseline: 18,    range: 1.2,  source: "BACnet" },
      { id: "OR4-TEMP",             label: "Theatre OR4 · room temp",         unit: "°C",  baseline: 19.0,  range: 0.3,  source: "Siemens Desigo" },
      { id: "BOILER-3-FLUE-TEMP",   label: "Boiler 3 · flue temp",            unit: "°C",  baseline: 142,   range: 5,    source: "Modbus TCP" },
      { id: "KITCHEN-EXTRACT-AMPS", label: "Kitchen extract fan · amps",      unit: "A",   baseline: 42,    range: 1.6,  source: "Modbus TCP" },
      { id: "HW-RETURN-TEMP",       label: "Domestic HW return temp",         unit: "°C",  baseline: 65,    range: 1.2,  source: "Aveva PI" },
    ],
  },
  environmental: {
    id: "environmental",
    name: "PFAS Investigation Site · Williamtown · NSW",
    short: "Environmental · PFAS",
    sector: "07",
    sector_label: "Environmental · PFAS · Bushfire · Flood",
    tags: [
      { id: "GW-BORE-7-PFAS",      label: "Bore 7 · PFAS · 24h composite",    unit: "ng/L",  baseline: 84,    range: 5,    source: "LIMS · Eurofins" },
      { id: "GW-BORE-12-PFAS",     label: "Bore 12 · PFAS · 24h composite",   unit: "ng/L",  baseline: 71,    range: 4,    source: "LIMS · Eurofins" },
      { id: "SOIL-MOIST-W3",       label: "Soil moisture · west zone 3",      unit: "%",     baseline: 22,    range: 1.2,  source: "Sentek IoT" },
      { id: "WIND-SPEED-1H",       label: "Wind speed · 1h average",          unit: "km/h",  baseline: 14,    range: 4,    source: "BoM AWAP" },
      { id: "RAINFALL-24H",        label: "Rainfall · 24h cumulative",        unit: "mm",    baseline: 1.6,   range: 1.4,  source: "BoM AWAP" },
      { id: "TREATMENT-PUMP-PRES", label: "Treatment plant · pump 2 disch",   unit: "kPa",   baseline: 380,   range: 10,   source: "Modbus TCP" },
      { id: "LEACHATE-FLOW",       label: "Leachate flow → treatment",        unit: "L/min", baseline: 22,    range: 2,    source: "OPC UA" },
    ],
  },
  // ── Phase 25.16m — Sportsbook / Dabble live ops profile ───────────
  // The DARA engine treats a sports-betting platform like any other
  // operational data estate: dozens of independent feeds, reconciliation
  // across them, anomaly flagging in real-time, regulator-grade audit
  // trail. Tags and units below model what a Friday-night NRL match
  // looks like in the ops room of an AU social bookmaker.
  sportsbook: {
    id: "sportsbook",
    name: "Friday Night NRL · Storm vs Broncos · 19:50 AEST",
    short: "Sports Betting · Live Book",
    sector: "08",
    sector_label: "Sports Betting · AU Wagering · Social Bookmaker",
    tags: [
      { id: "BET-RATE-1M",       label: "Bet acceptance rate · 1-min",          unit: "bps",   baseline: 1_240,  range: 180,   source: "Internal odds engine" },
      { id: "BOOK-LIAB-MATCH",   label: "Book liability · match · AUD",         unit: "k AUD", baseline: 380,    range: 25,    source: "Risk engine · live" },
      { id: "BET-ACCEPT-LAT",    label: "Bet acceptance latency · p95",         unit: "ms",    baseline: 142,    range: 20,    source: "App perf monitor" },
      { id: "AUSTRAC-SMR-Q",     label: "AUSTRAC SMR queue · pending",          unit: "ev",    baseline: 6,      range: 2,     source: "AML pipeline · Senet" },
      { id: "NCPF-HARM-FLAGS",   label: "NCPF harm-signal flags · 15m",         unit: "ev",    baseline: 4,      range: 1.5,   source: "Responsible-gambling lyr" },
      { id: "BETSTOP-CHECK-LAT", label: "BetStop register check · p95",         unit: "ms",    baseline: 92,     range: 12,    source: "BetStop API" },
      { id: "COPY-BET-CASCADE",  label: "Copy-bet cascade size · top tipster",  unit: "bets",  baseline: 38,     range: 8,     source: "Social graph DB" },
    ],
  },
};

// Phase 25.16f — Map iron-ore + water profiles to Ventia sector codes
// where they already cover the sector (so the 7-sector dashboard can
// pull the right profile per sector without duplicating data).
SITE_PROFILES.ironore.sector = "04";
SITE_PROFILES.ironore.sector_label = "Energy & Resources · Conveyors · Fixed Plant";
SITE_PROFILES.water.sector = "03";
SITE_PROFILES.water.sector_label = "Water Utilities · Treatment · Reticulation";

// Per-tag anomaly classifier mirrors the production logic. Thresholds
// chosen so the simulator drifts into "high" status every 12-25 seconds
// to produce a visible, defensible flag stream for investor demos
// without feeling artificial.
function classify(pct) {
  if (Math.abs(pct) >= 15) return { tone: "high", label: "ANOMALY · INVESTIGATE" };
  if (Math.abs(pct) >= 8) return { tone: "watch", label: "DRIFT · MONITOR" };
  return { tone: "ok", label: "NOMINAL" };
}

const ARCH_STEPS = [
  { code: "01", title: "Read at source", body: "DARA's OPC UA / Modbus / PI Web API connector reads tags every 30-60 seconds from inside your DMZ. Read-only client, never writes. No PLC touch." },
  { code: "02", title: "Encrypt + tunnel", body: "TLS 1.3 outbound to AU-sovereign DARA cloud. Tenant-isolated workspace. AES-256 at rest. No multi-tenant data co-mingling." },
  { code: "03", title: "Reconcile + flag", body: "DARA Engine compares each tag against historical baseline, cross-site benchmarks, and tag-pair correlations (shovel cycle ↔ truck payload ↔ belt rate). Anomalies graded." },
  { code: "04", title: "Brief at 06:00", body: "Operations superintendent's inbox gets a one-page reconciled brief at 06:00 local each morning. Variance, root-cause hypothesis, action items. Audit-defensible." },
];

const HONEST_LIMITS = [
  "DARA does not run a PLC. It does not actuate equipment. It does not write to your historian.",
  "DARA does not replace your control room. It supplements the morning superintendent meeting.",
  "DARA cannot ingest tags it doesn't have read access to. Scope is whatever your IT team exposes.",
  "Tier-2 site (≤200 critical tags · existing historian) onboards in 3-4 weeks. Greenfield Modbus-only sites in 6-8 weeks.",
];

function fmtNum(v, unit) {
  if (unit === "tph" || unit === "t") return Math.round(v).toLocaleString("en-AU");
  if (unit === "kt") return v.toFixed(1);
  if (unit === "s" || unit === "mm/s") return v.toFixed(1);
  if (unit === "A" || unit === "kPa") return Math.round(v).toLocaleString("en-AU");
  if (unit === "°C" || unit === "L/s") return Math.round(v).toLocaleString("en-AU");
  if (unit === "m" || unit === "pH") return v.toFixed(2);
  // Phase 25.16f — Ventia sector profile units (transport / telco / defence / social / environmental)
  if (unit === "ms" || unit === "ev" || unit === "kW" || unit === "ng/L" ||
      unit === "km/h" || unit === "L/min" || unit === "Pa") {
    return Math.round(v).toLocaleString("en-AU");
  }
  if (unit === "ppm" || unit === "dBm" || unit === "V" || unit === "%" || unit === "mm") {
    return v.toFixed(v < 1 ? 2 : 1);
  }
  if (unit === "") return v.toFixed(2); // dimensionless (COP)
  // Phase 25.16m — Sportsbook units
  if (unit === "bps" || unit === "k AUD" || unit === "bets") {
    return Math.round(v).toLocaleString("en-AU");
  }
  return String(v);
}

// Profile-aware brief writers — each one knows the failure-mode language
// of its industry. This is closer to how the production DARA Engine writes
// briefs: not "X% deviation", but "cavitation signature · seal failure
// likely · dispatch maintenance crew".
function ironoreNarrative(flagged, worst) {
  if (worst.id === "BELT-7B-SCALE" && worst.pct < 0) return "Likely cause: shovel-side hang-up or upstream feed disruption. Action: dispatch geotech to Shovel 442 face.";
  if (worst.id === "SHOVEL-442-CYCLE") return "Likely cause: face condition deterioration or operator pattern drift. Action: rotate shovel onto 24-hour monitoring.";
  if (worst.id === "TRUCK-612-PAYLOAD") return "Likely cause: payload sensor drift or load-out underfill. Action: re-calibrate truck 612 weighbridge next swing.";
  if (worst.id === "CRUSHER-2-AMPS" && worst.pct > 0) return "Likely cause: harder feed than blast design predicted, or wear-part degradation. Action: schedule mantle inspection.";
  return "Likely cause: multi-signal pit-side disruption. Action: superintendent walk-down at 07:00 muster.";
}
function waterNarrative(flagged, worst) {
  const idsFlagged = flagged.map(f => f.id);
  // Multi-signal leak signature
  if (idsFlagged.includes("B2-DISCH-PRESS") && idsFlagged.includes("RM-FLOW-DELIVERY")) {
    return "Multi-signal leak signature: discharge pressure dropping with flow delivery shortfall = mass-balance gap on rising main. Likely cause: air-release valve failure or rising-main rupture between chainage 4.2-6.8 km. Action: dispatch leak-detection crew along AV-RM-4B → AV-RM-4D within 4 hours.";
  }
  // Cavitation signature
  if (idsFlagged.includes("B2-VIBRATION") && (idsFlagged.includes("B2-DISCH-PRESS") || idsFlagged.includes("B2-MOTOR-AMPS"))) {
    return "Cavitation signature: axial vibration rising with discharge-pressure instability. Likely cause: net positive suction head margin lost — air ingress or wet-well draw-down. Action: inspect suction strainer and wet-well makeup, target completion before next pumping cycle.";
  }
  // Bearing wear
  if (worst.id === "B2-BEARING-TEMP" || (worst.id === "B2-VIBRATION" && worst.pct > 15)) {
    return "Bearing degradation signature: temperature and vibration rising together. Estimated 2-7 days to seal-water failure at current rate. Action: schedule planned shutdown on Booster 2, route flow via Booster 3 backup.";
  }
  // Phase 25.14c — chemical-dose drift. Chlorine residual below band is a
  // public-health risk on the way out, above band is chemical-cost burn.
  if (worst.id === "CHLOR-RES-MGL") {
    if (worst.pct < 0) {
      return "Chlorine-dose under-injection: post-dose residual tracking 8-15% below target band. Risk: regulatory non-compliance on free chlorine residual at network extremities within 6-12 hours. Likely cause: chemical-feed pump head wear or empty drum. Action: dispatch dosing-room check this shift, verify Cl₂ drum levels and pump-1 stroke.";
    }
    return "Chlorine-dose over-injection: post-dose residual tracking above band. Likely cause: dosing-pump stuck at full stroke or pH compensation drift. Action: switch to standby dosing pump, log chemical-cost variance for the cost-per-ML reconciliation.";
  }
  // Phase 25.14c — blower energy waste. Aeration blowers are the #1 energy
  // line on a WWTP; sustained current creep = wasted kWh + DO setpoint drift.
  if (worst.id === "BLOWER-1-AMPS" && worst.pct > 0) {
    return "Aeration blower 1 energy creep: motor amps trending +8-15% above 30-day baseline at unchanged DO setpoint. Likely cause: diffuser fouling or blower-bearing wear forcing more current to hit the same airflow. Estimated waste at current trend: ~$2.4-3.6k/month per blower in energy cost. Action: schedule diffuser cleaning + blower vibration walk-down this week.";
  }
  // Phase 25.14c — throughput anomaly. Standalone flow shortfall (no
  // pressure correlation) = customer-side or upstream supply issue.
  if (worst.id === "RM-FLOW-DELIVERY" && worst.pct < 0 && !idsFlagged.includes("B2-DISCH-PRESS")) {
    return "Throughput anomaly: rising-main flow delivery 8-15% below 30-day baseline with discharge pressure inside band. Likely cause: downstream customer draw drop (reservoir 7 saturated) OR network valve partially closed. Action: cross-check Reservoir 7 level vs. demand forecast, walk valves V-RM-3 and V-RM-5.";
  }
  // Phase 25.16f — Booster-pump catch-all (added after testing-agent
  // surfaced standalone B2-DISCH/SUCT/AMPS gaps). Keeps the brief
  // sector-flavoured even when inject randomly lands on a single
  // booster-pump tag without a correlated co-flag.
  if (worst.id.startsWith("B2-")) {
    return `Booster pump signal drift on ${worst.label}: likely seal-wear or upstream draw shift. Action: trend for 30 minutes before dispatching a pump tech, cross-check Booster 2 alongside the rest of the booster pad.`;
  }
  return `Single-signal anomaly on ${worst.label}. Recommend trending for next 6 hours before action.`;
}
function goldNarrative(flagged, worst) {
  const idsFlagged = flagged.map(f => f.id);
  if (idsFlagged.includes("DEWATER-P3-VIB") && idsFlagged.includes("DEWATER-P3-AMPS")) {
    return "Dewatering pump 3 cavitation signature: vibration rising with motor amps swinging. At 850 RL with no backup pump, this is a fortnight-critical fault. Action: dispatch pump tech to 850 RL by next shift, prep Pump 4 hot standby.";
  }
  if (worst.id === "VENT-FAN-2-AMPS") return "Vent fan 2 amp drift = blade icing or duct restriction. Action: inspect intake screens, verify ventilation balance with surveyor.";
  if (worst.id === "CIL-TANK-3-PH") return "CIL tank pH drift = cyanide consumption anomaly. Action: increase lime dose, sample for free CN within next hour. Compliance: log against ICMC code 6.3.";
  if (worst.id === "MILL-1-MOTOR-AMPS" && worst.pct > 0) return "SAG mill amp creep = liner wear approaching reline window or feed coarsening. Action: cross-check P80 from last sampling, schedule reline if liner wear >85%.";
  if (worst.id === "MILL-FEED-RATE") return "Mill feed conveyor drift = upstream surge bin level issue or feeder belt slip. Action: inspect conveyor 5 head pulley, check feeder bin level.";
  if (worst.id === "TAILINGS-PUMP-PRES") return "Tailings discharge pressure shift = pipeline blockage forming or pump wear. Action: schedule line flush, inspect impeller next shutdown.";
  return `Single-signal anomaly on ${worst.label}. Recommend trending for next 6 hours before action.`;
}
const PROFILE_NARRATORS = {
  ironore: ironoreNarrative,
  water: waterNarrative,
  gold: goldNarrative,
  // Phase 25.16f — Ventia 7-sector narrators
  transport: transportNarrative,
  telco: telcoNarrative,
  defence: defenceNarrative,
  social: socialNarrative,
  environmental: environmentalNarrative,
  // Phase 25.16m — Sports betting narrator
  sportsbook: sportsbookNarrative,
};

// ── Phase 25.16f — Ventia 7-sector narrative writers ──────────────────
// Each narrator knows the failure-mode vocabulary of its sector. Keep
// signatures identical to the existing iron-ore / water / gold writers
// so they slot into PROFILE_NARRATORS without any consumer changes.
function transportNarrative(flagged, worst) {
  const ids = flagged.map(f => f.id);
  if (worst.id === "CO-EAST-PORTAL" && worst.pct > 0) {
    return "CO concentration above ventilation setpoint at east portal. Likely cause: traffic incident OR jet-fan underperformance. RISK: tunnel-closure trigger if sustained beyond 10 min. Action: tunnel control room to verify fan-stack output, escalate to TfNSW within 5 min.";
  }
  if ((ids.includes("JET-FAN-3-AMPS") || ids.includes("JET-FAN-7-AMPS")) && worst.pct > 0) {
    return "Jet fan motor amp creep: blade fouling, duct obstruction, or pressure rebalance event. Action: schedule fan-stack inspection at next 02:00 maintenance window, cross-check CO trend in 30 min.";
  }
  if (worst.id === "VMS-SIGN-12-LATENCY" && worst.pct > 0) {
    return "VMS sign comms degraded: heartbeat latency above SLA threshold. Risk: sign goes dark → TfNSW SLA breach. Action: dispatch comms tech to north-side cabinet within 4 hours.";
  }
  if (worst.id === "CCTV-PTZ-7-UPTIME" && worst.pct < 0) {
    return "CCTV PTZ 7 uptime degraded: camera dropping frames or PoE switch fault. Security + incident-response risk. Action: failover to PTZ 8 view, dispatch camera tech this shift.";
  }
  if (worst.id === "LIGHTING-CKT-3-LOAD" && worst.pct < 0) {
    return "Lighting circuit 3 load drop: cluster of luminaires failed (likely 4-7 fittings out). Action: night-shift bulb-replacement bundle, prevent tunnel-darkness incident.";
  }
  if (worst.id === "NOX-MIDPOINT" && worst.pct > 0) {
    return "NOx midpoint trending above threshold: ventilation imbalance suspected. Action: rebalance jet-fan stack output, review traffic-flow profile for last 30 min.";
  }
  return `Single-signal drift on ${worst.label}. Recommend trending for next 30 minutes before action.`;
}

function telcoNarrative(flagged, worst) {
  const ids = flagged.map(f => f.id);
  if (ids.includes("BACKHAUL-LINK-A-UTIL") && ids.includes("BACKHAUL-LINK-B-UTIL")) {
    return "Backhaul saturation signature: both upstream links above 80% utilisation simultaneously. Customer SLA risk imminent. Action: invoke traffic-engineering reroute to POI 408, raise capacity-augment ticket with carrier engineering team this shift.";
  }
  if (worst.id === "OPT-RX-NODE-47") {
    return "Optical receive power deviation at Node 47: fibre attenuation, dirty connector, or OLT laser ageing. Action: dispatch fibre tech this maintenance window, pre-emptive connector clean + splice check.";
  }
  if (worst.id === "BTS-HO-FAIL-15M" && worst.pct > 0) {
    return "Failed-handover spike: cell sector degradation OR neighbour-cell config drift. Action: pull KPI logs for last hour, validate handover thresholds against neighbour matrix.";
  }
  if (worst.id === "BATT-STRING-V" && worst.pct < 0) {
    return "Battery backup string voltage falling: sulphation or single-cell failure. RISK: tower offline on next mains-power event. Action: dispatch battery-replacement van within 48 hours.";
  }
  if (worst.id === "TOWER-AC-AMPS") {
    return "Tower AC current draw drift: load shift or rectifier degradation. Action: cross-check rectifier health, log against site-energy budget.";
  }
  if (worst.id === "ALARM-RATE-15M" && worst.pct > 0) {
    return "Alarm rate elevated: noise floor in NMS or cascading fault upstream. Action: NMS engineer to correlate alarm cluster, suppress flapping links pending RCA.";
  }
  return `Single-signal drift on ${worst.label}. Recommend trending for next 30 minutes before action.`;
}

function defenceNarrative(flagged, worst) {
  if (worst.id === "CHILLER-1-COP" && worst.pct < 0) {
    return "Chiller 1 efficiency degraded: refrigerant charge low, condenser fouling, or compressor wear. Action: dispatch base-services mechanical tech, log against energy-cost budget. No operational impact.";
  }
  if (worst.id === "RING-MAIN-PRESSURE" && worst.pct < 0) {
    return "Base ring-main pressure drop: leak or major draw event. Action: cross-check meter loggers against base water plan, dispatch utilities crew. Standard non-classified utility response.";
  }
  if (worst.id === "FEEDER-A-LOAD" && worst.pct > 0) {
    return "Feeder A load spike above 30-day band: unplanned base electrical event. Action: notify base utilities supervisor; brief stays within non-classified envelope.";
  }
  if (worst.id === "GENERATOR-1-FUEL" && worst.pct < 0) {
    return "Generator 1 fuel level dropping faster than monthly burn rate: possible leak or unauthorised draw. Action: tank-dip inspection within 24h, reorder if confirmed.";
  }
  if (worst.id === "MESS-HW-RETURN-TEMP" && worst.pct < 0) {
    return "Mess hall hot-water return temp below 60°C threshold: Legionella compliance risk on shared accommodation block. Action: validate boiler setpoint, flush dead-legs within 24h.";
  }
  if (worst.id === "RTU-12-FAN-AMPS") {
    return "RTU 12 supply fan amp drift: filter loading or belt slip. Action: schedule belt + filter swap at next mess-hall PMV.";
  }
  return `Single-signal drift on ${worst.label}. Standard utilities response — no operational sensitivity.`;
}

function socialNarrative(flagged, worst) {
  if (worst.id === "ICU-AHU-DELTA-P" && worst.pct < 0) {
    return "ICU AHU differential pressure collapsing: infection-control pressure cascade compromised. CRITICAL: contamination risk to vulnerable patient cohort. Action: dispatch IFM mechanical SUPERVISOR within 1 hour, notify infection-control lead immediately.";
  }
  if (worst.id === "OR4-TEMP" && Math.abs(worst.pct) > 8) {
    return "Theatre OR4 room temp drifting outside 18.5-19.5°C surgical spec band. RISK: theatre booking may be cancelled by surgical team. Action: dispatch theatre-services tech NOW, escalate to OR coordinator.";
  }
  if (worst.id === "HW-RETURN-TEMP" && worst.pct < 0) {
    return "Domestic HW return temp below 60°C: Legionella compliance risk on shared ward HW system. Action: validate boiler setpoint + recirculation pump, dispatch within 24h, log against AS/NZS 3666.";
  }
  if (worst.id === "AHU-12-SUPPLY-TEMP" && Math.abs(worst.pct) > 10) {
    return "AHU 12 supply temp drift: ward IAQ + patient-comfort impact. Action: cross-check chilled-water valve actuation, schedule mechanical inspection.";
  }
  if (worst.id === "CHILLER-PLANT-COP" && worst.pct < 0) {
    return "Chiller plant efficiency drop: increased operating cost OR approaching failure mode. Action: dispatch chiller mechanical tech, log against energy + NABERS budget.";
  }
  if (worst.id === "BOILER-3-FLUE-TEMP" && worst.pct > 0) {
    return "Boiler 3 flue temp rising: combustion efficiency loss (scale build-up or burner fouling). Action: schedule combustion service at next PMV window.";
  }
  if (worst.id === "KITCHEN-EXTRACT-AMPS") {
    return "Kitchen extract fan amp drift: filter loading or grease build-up. Action: schedule canopy clean + filter swap; fire-safety priority.";
  }
  return `Single-signal drift on ${worst.label}. Recommend trending for next 30 minutes before action.`;
}

function environmentalNarrative(flagged, worst) {
  const ids = flagged.map(f => f.id);
  if ((worst.id === "GW-BORE-7-PFAS" || worst.id === "GW-BORE-12-PFAS") && worst.pct > 0) {
    return "PFAS concentration spike at groundwater monitoring bore: plume migration suspected. REGULATORY: NSW EPA notification required within 24h if confirmed above 70 ng/L threshold. Action: re-sample within 4h, escalate to environmental lead, prepare regulatory comms.";
  }
  if (ids.includes("RAINFALL-24H") && ids.includes("SOIL-MOIST-W3")) {
    return "Infiltration event detected: 24h rainfall above forecast AND soil moisture rising. Expect leachate flow to rise within 4-6h, treatment plant load to increase. Action: pre-stage treatment-plant operator, validate chemical inventory.";
  }
  if (worst.id === "LEACHATE-FLOW" && worst.pct > 0) {
    return "Leachate flow to treatment trending above design rate: treatment-plant overload risk. Action: throttle inflow at sump, ready bunded storage. Document under Section 60 reporting.";
  }
  if (worst.id === "TREATMENT-PUMP-PRES") {
    return "Treatment plant pump 2 discharge drift: GAC-bed fouling OR pump wear. Action: GAC backwash + pump-impeller inspection at next maintenance window.";
  }
  if (worst.id === "WIND-SPEED-1H" && worst.pct > 0) {
    return "Wind speed elevated above 30-day band: bushfire-risk cross-reference active. Action: notify on-site fire warden, defer hot work until wind drops.";
  }
  return `Single-signal drift on ${worst.label}. Recommend trending for next 30 minutes before action.`;
}

// ── End Phase 25.16f narrators ────────────────────────────────────────

// ── Phase 25.16m — Sports-betting narrator (Dabble showcase) ─────────
// AU social bookmaker live ops. The narrator speaks the language of
// trading floor, AUSTRAC compliance officers, and responsible-gambling
// intervention teams — not industrial maintenance crews.
function sportsbookNarrative(flagged, worst) {
  const ids = flagged.map(f => f.id);
  if (worst.id === "BOOK-LIAB-MATCH" && worst.pct > 0) {
    return "Match liability above pre-set band: heavy one-sided action on the favourite OR sharp money on the underdog. RISK: exposure breach before end of first quarter. Action: trading desk to widen overround OR layoff via exchange immediately, confirm risk-officer is on the floor.";
  }
  if (worst.id === "AUSTRAC-SMR-Q" && worst.pct > 0) {
    return "AUSTRAC SMR queue elevated: anti-money-laundering pipeline flagging suspicious transactions above baseline. Likely deposit-bet-withdraw cycle pattern OR rapid wallet-funding from new payment source. Action: AML team to fast-track top 3 SMRs ahead of the 24h reporting clock, brief MLRO before close.";
  }
  if (worst.id === "NCPF-HARM-FLAGS" && worst.pct > 0) {
    return "NCPF harm-signal spike: customers crossing intervention threshold in the last 15 minutes (chasing-loss pattern, session-length runaway, deposit velocity). Action: responsible-gambling team to trigger automated nudge → manual outreach for top 3 cases. Document under National Consumer Protection Framework intervention log.";
  }
  if (worst.id === "BET-ACCEPT-LAT" && worst.pct > 0) {
    return "Bet acceptance latency above SLA: customers experiencing slow bet confirmation on live markets. RISK: customer complaints, churn signal, ACMA service-standard implication. Action: SRE on call to validate odds-engine + risk-engine path; consider rate-limiting non-priority markets until resolved.";
  }
  if (worst.id === "COPY-BET-CASCADE" && worst.pct > 0) {
    return "Social copy-bet cascade above normal: one tipster's bet has triggered an outsized follower cascade in a short window. Watch for coordinated inauthentic behaviour OR concentration risk if the underlying outcome resolves against the book. Action: trading desk to check market exposure on cascaded outcome; integrity team to flag tipster account for review.";
  }
  if (worst.id === "BETSTOP-CHECK-LAT" && worst.pct > 0) {
    return "BetStop register check latency above threshold: federally-mandated self-exclusion lookup degrading. RISK: failure to detect self-excluded customer at sign-up or bet-placement = direct ACMA breach. Action: dispatch infra on-call NOW, fail-closed if degradation persists beyond 5 minutes.";
  }
  if (worst.id === "BET-RATE-1M" && worst.pct < 0) {
    return "Bet acceptance rate dropping below match-time baseline: app performance issue OR market-suspension cascade OR customer-side outage on a major ISP. Action: cross-check with app perf monitor + carrier status, escalate to comms team if customer-facing impact suspected.";
  }
  return `Single-signal drift on ${worst.label}. Trading desk to monitor for next 5 minutes before escalation.`;
}

// Phase 25.16f — Export site profiles + helpers so the Ventia 7-sector
// dashboard (Ventia7SectorDemo.jsx) can reuse the same engine without
// duplicating tag / narrator / classification logic.
export { SITE_PROFILES, PROFILE_NARRATORS, classify, buildBrief, fmtNum };

function buildBrief(tags, siteName, profileId) {
  const flagged = tags.filter(t => t.flag.tone !== "ok");
  const tsLabel = `${siteName} · ${new Date().toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric" })} · 06:00 AWST.`;
  if (flagged.length === 0) {
    return `${tsLabel} All ${tags.length} critical tags within nominal band over the last 30 minutes. No action items.`;
  }
  const worst = flagged.reduce((a, b) => (Math.abs(b.pct) > Math.abs(a.pct) ? b : a));
  const directions = flagged.map(t => `${t.label} ${t.pct > 0 ? "+" : ""}${t.pct.toFixed(1)}%`).join(" · ");
  const narrator = PROFILE_NARRATORS[profileId] || ironoreNarrative;
  const narrative = narrator(flagged, worst);
  return `${tsLabel} ${flagged.length} of ${tags.length} tags outside nominal band over the last 30 minutes. ${directions}. Largest deviation: ${worst.label} (${worst.pct > 0 ? "+" : ""}${worst.pct.toFixed(1)}% vs. 30-day baseline). ${narrative}`;
}

// Phase 25.13ay — Snapshot encode / decode. Client-side base64 URL of the
// full demo state so the operator can freeze a screenshare-worthy moment
// and share it as a self-contained shareable URL. No backend required —
// the URL itself carries every flagged tag value + the brief text.
//
// Schema v1: { v, p (profileId), t (timestamp), x (tags array: [id, value]), b (brief text) }
function encodeSnapshot(profileId, tags, briefText) {
  const payload = {
    v: 1,
    p: profileId,
    t: Date.now(),
    x: tags.map(t => [t.id, Number(t.value.toFixed(3))]),
    b: briefText,
  };
  try {
    const json = JSON.stringify(payload);
    // btoa requires latin-1 — encode UTF-8 first via encodeURIComponent dance.
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return null;
  }
}

function decodeSnapshot(encoded) {
  try {
    const json = decodeURIComponent(escape(atob(encoded)));
    const payload = JSON.parse(json);
    if (payload.v !== 1) return null;
    if (!SITE_PROFILES[payload.p]) return null;
    return payload;
  } catch {
    return null;
  }
}

export default function IndustrialConnectorDemo() {
  usePageview("/industrial-demo");
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 25.13ay — replay mode is DERIVED directly from the URL so it stays
  // consistent across React StrictMode double-mounts and any future hot-reload
  // edge cases. No useState — single source of truth is the URL.
  const snapshotParam = searchParams.get("snapshot");
  const replayInfo = snapshotParam ? decodeSnapshot(snapshotParam) : null;
  const isReplay = Boolean(replayInfo);

  const [running, setRunning] = useState(!isReplay);
  const [tickCount, setTickCount] = useState(0);
  // Phase 25.13ax — profile-aware. Operator can switch between iron ore /
  // water utility / underground gold during a screenshare so each prospect
  // sees their own industry's failure modes & language.
  // Phase 25.16f — `?profile=X` entry-point param lets deep-dive links
  // from /ventia and /ventia/demo land directly on the right sector.
  const initialProfileFromUrl = (() => {
    const p = searchParams.get("profile");
    return p && SITE_PROFILES[p] ? p : null;
  })();
  const [profileId, setProfileId] = useState(
    replayInfo?.p || initialProfileFromUrl || "ironore",
  );
  const profile = SITE_PROFILES[profileId];
  const siteName = profile.name;
  const [tags, setTags] = useState(() => profile.tags.map(t => ({
    ...t,
    value: t.baseline,
    pct: 0,
    flag: classify(0),
    history: [t.baseline],
    injectedTicks: 0,
    injectedDirection: 1,
  })));

  // Phase 25.13ay — Hydrate from snapshot whenever replayInfo is present.
  // Single effect, runs once per replay-mode entry. Stable across StrictMode
  // double-mounts because it's idempotent (always sets the same values).
  useEffect(() => {
    if (!isReplay || !replayInfo?.x) return;
    const p = SITE_PROFILES[replayInfo.p];
    if (!p) return;
    const map = Object.fromEntries(replayInfo.x);
    setProfileId(replayInfo.p);
    setTags(p.tags.map(t => {
      if (map[t.id] != null) {
        const v = map[t.id];
        const pct = ((v - t.baseline) / t.baseline) * 100;
        return {
          ...t, value: v, pct, flag: classify(pct), history: [t.baseline, v],
          injectedTicks: 0, injectedDirection: 1,
        };
      }
      return {
        ...t, value: t.baseline, pct: 0, flag: classify(0), history: [t.baseline],
        injectedTicks: 0, injectedDirection: 1,
      };
    }));
    setDisplayedBrief(replayInfo.b || "");
    setIsStreaming(false);
    setRunning(false);
  }, [isReplay, replayInfo?.p, replayInfo?.b]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Re-initialize tags whenever profile changes. Skip entirely while in
  // replay mode so the snapshot-hydrated tag values are preserved
  // (StrictMode double-mounts the effect on every load — a one-shot ref
  // guard isn't enough).
  useEffect(() => {
    if (isReplay) return;
    const p = SITE_PROFILES[profileId];
    setTags(p.tags.map(t => ({
      ...t,
      value: t.baseline,
      pct: 0,
      flag: classify(0),
      history: [t.baseline],
      injectedTicks: 0,
      injectedDirection: 1,
    })));
    setBriefHistory([]);
    setDisplayedBrief("");
    setBriefTrigger(b => b + 1);
  }, [profileId, isReplay]);

  // Phase 25.13aw — Demo-only anomaly injection. Lets the operator force a
  // +35% spike on a chosen tag (or a random one) for the next 3 ticks while
  // screensharing with investors / superintendents. After the countdown,
  // natural mean-reversion takes over and the tag drifts back to baseline
  // organically — exactly what you'd see on a real site after the upstream
  // issue is resolved.
  const injectAnomaly = (tagId, direction = 1) => {
    setTags(prev => prev.map(t =>
      t.id === tagId ? { ...t, injectedTicks: 3, injectedDirection: direction } : t,
    ));
    setBriefTrigger(b => b + 1);  // regenerate the brief immediately
  };
  const injectRandom = () => {
    const candidate = tags[Math.floor(Math.random() * tags.length)];
    const direction = Math.random() > 0.5 ? 1 : -1;
    injectAnomaly(candidate.id, direction);
  };

  // Streaming auto-brief mirrors the production pattern: word-by-word
  // typing with cancellation guard for re-renders.
  const [displayedBrief, setDisplayedBrief] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const briefTokenRef = useRef(0);
  // Phase 25.13ax — kept-brief history (last 6) and Hold-brief toggle so the
  // user can read the brief without it churning during a screenshare.
  const [briefHistory, setBriefHistory] = useState([]);
  const [briefHeld, setBriefHeld] = useState(false);
  // Phase 25.13aw — separate trigger so the brief can regenerate IMMEDIATELY
  // on inject (not waiting for the next tick cycle). Bumped by both the
  // tick loop (throttled) and the inject handlers (instant).
  const [briefTrigger, setBriefTrigger] = useState(0);

  // Tick the simulator every 1.5s while running. Tags drift continuously;
  // the brief, however, does NOT auto-regenerate any more — Phase 25.13az.
  // The operator gets a stable brief after streaming completes and decides
  // when to refresh (via the Refresh button or by injecting an anomaly).
  // This solves the "missed the brief before it overwrote" problem.
  useEffect(() => {
    if (!running || isReplay) return undefined;
    const id = setInterval(() => {
      setTickCount(c => c + 1);
      setTags(prev => prev.map(t => {
        // Brownian-ish drift around baseline, occasionally larger excursions
        const driftMag = (Math.random() - 0.5) * t.range * 0.3;
        const occasionalSpike = Math.random() < 0.07 ? (Math.random() - 0.5) * t.range * 2.2 : 0;
        // Phase 25.13aw — if a demo-inject is active, override with a strong
        // 35% deviation for 3 ticks, then let mean-reversion handle the recovery.
        let injectionOffset = 0;
        let nextInjectedTicks = t.injectedTicks;
        if (t.injectedTicks > 0) {
          injectionOffset = t.injectedDirection * t.baseline * 0.35;
          nextInjectedTicks = t.injectedTicks - 1;
        }
        const nextValue = t.value + driftMag + occasionalSpike + injectionOffset;
        // Mean-revert gently toward baseline (slower while injected so the
        // anomaly visibly persists for the demo).
        const revertStrength = nextInjectedTicks > 0 ? 0.04 : 0.18;
        const reverted = nextValue + (t.baseline - nextValue) * revertStrength;
        const pct = ((reverted - t.baseline) / t.baseline) * 100;
        const flag = classify(pct);
        const history = [...t.history.slice(-23), reverted];
        return { ...t, value: reverted, pct, flag, history, injectedTicks: nextInjectedTicks };
      }));
    }, 1500);
    return () => clearInterval(id);
  }, [running]);

  // Re-stream the brief whenever briefTrigger changes — either from the
  // throttled tick loop (~15s) or from a manual inject (immediate).
  // Skip if held (demo pause) or in replay mode (snapshot hydrates the
  // brief text directly, no streaming needed).
  useEffect(() => {
    if (briefHeld) return undefined;
    if (isReplay) {
      // Replay mode: show the captured brief text immediately, no streaming.
      if (replayInfo?.b) {
        setDisplayedBrief(replayInfo.b);
        setIsStreaming(false);
      }
      return undefined;
    }
    const brief = buildBrief(tags, siteName, profileId);
    const tokens = brief.split(" ");
    const myToken = ++briefTokenRef.current;
    setIsStreaming(true);
    setDisplayedBrief("");
    let idx = 0;
    const timers = [];
    const tick = () => {
      if (briefTokenRef.current !== myToken) return;
      if (idx >= tokens.length) {
        setIsStreaming(false);
        // Append the just-completed brief to the history (avoid duplicates).
        setBriefHistory(prev => {
          if (prev[0]?.text === brief) return prev;
          const entry = {
            id: `b-${Date.now()}`,
            text: brief,
            generatedAt: new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          };
          return [entry, ...prev].slice(0, 6);
        });
        return;
      }
      const next = idx === 0 ? tokens[idx] : ` ${tokens[idx]}`;
      setDisplayedBrief(prev => prev + next);
      idx += 1;
      timers.push(setTimeout(tick, 28));
    };
    timers.push(setTimeout(tick, 100));
    return () => {
      briefTokenRef.current += 1;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefTrigger, briefHeld, profileId, isReplay, replayInfo]);

  // Snapshot save — freeze the current moment into a shareable URL.
  // Regenerate the brief from CURRENT tags so the snapshot's tag values
  // and brief text are internally consistent (no drift from mean-reversion
  // between a held brief and the save click).
  const saveSnapshot = () => {
    const briefText = buildBrief(tags, siteName, profileId);
    const encoded = encodeSnapshot(profileId, tags, briefText);
    if (!encoded) {
      toast.error("Snapshot failed to encode");
      return;
    }
    const url = `${window.location.origin}/industrial-demo?snapshot=${encoded}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Snapshot URL copied to clipboard · paste into email or chat"),
      () => toast.error("Couldn't copy — URL is in the address bar instead"),
    );
    setSearchParams({ snapshot: encoded }, { replace: true });
  };

  // Exit replay mode — clear the URL param. The derived isReplay/replayInfo
  // will both flip to null/false on the next render, the profile-change
  // effect will reset tags to baseline, and the simulation resumes.
  const resumeLive = () => {
    setSearchParams({}, { replace: true });
    setRunning(true);
    setBriefTrigger(b => b + 1);
  };

  const flaggedCount = tags.filter(t => t.flag.tone !== "ok").length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="industrial-demo-page">
      {/* Replay banner — visible only when hydrated from snapshot URL */}
      {isReplay && (
        <div className="bg-[#FFB000] text-black px-4 sm:px-6 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="industrial-demo-replay-banner">
          <div className="flex items-center gap-3">
            <Camera className="w-4 h-4 flex-shrink-0" />
            <div className="font-mono text-[11px] uppercase tracking-widest">
              REPLAY MODE · FROZEN SNAPSHOT
              {replayInfo?.t && (
                <span className="ml-3 normal-case opacity-70">
                  captured {new Date(replayInfo.t).toLocaleString("en-AU", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={resumeLive}
            data-testid="industrial-demo-resume-live"
            className="font-mono text-[10px] uppercase tracking-widest bg-black text-[#FFB000] px-3 py-1.5 hover:opacity-90 flex items-center gap-1.5"
          >
            <Play className="w-3 h-3" /> Resume live demo
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="industrial-demo-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · INDUSTRIAL CONNECTOR · LIVE DEMO
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/industrial/connector" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000]" data-testid="industrial-demo-to-pilot">
              Start a 12-week pilot
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // LIVE DEMO · STREAMING FROM A SIMULATED TIER-2 WA IRON-ORE SITE
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-4xl">
            DARA, reading your historian. <span className="text-[#FFB000]">Read-only. No PLC touch. Live in 4 weeks.</span>
          </h1>
          <p className="text-sm sm:text-base text-zinc-300 mt-5 max-w-3xl leading-relaxed">
            {tags.length} critical tags streaming every 1.5 seconds. Anomalies graded in real time. Auto-brief regenerated continuously. <span className="text-zinc-500">All data simulated client-side — no network, no real mine. The architecture below is what runs on real sites today.</span>
          </p>

          {/* Profile selector — pick the industry the audience cares about */}
          <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="industrial-demo-profile-selector">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-1">// SITE PROFILE</span>
            {Object.values(SITE_PROFILES).map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => setProfileId(p.id)}
                data-testid={`industrial-demo-profile-${p.id}`}
                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  profileId === p.id
                    ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                    : "border-zinc-700 text-zinc-400 hover:border-[#FFB000] hover:text-[#FFB000]"
                }`}
              >
                {p.short}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            {["OPC UA", "OSISOFT PI WEB API", "MODBUS TCP", "MQTT / SPARKPLUG B", "READ-ONLY", "TLS 1.3", "AU-SOVEREIGN"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Status band */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900" data-testid="industrial-demo-status">
            <StatusCell label="SITE" value={siteName} accent />
            <StatusCell label="ACTIVE TAGS" value={`${tags.length}`} />
            <StatusCell label="UPDATE INTERVAL" value="1.5 s" />
            <StatusCell label="TICKS · THIS SESSION" value={tickCount} />
            <StatusCell
              label="FLAGGED · NOW"
              value={`${flaggedCount} / ${tags.length}`}
              highlight={flaggedCount > 0}
            />
          </div>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
              <span className={`w-2 h-2 rounded-full ${running ? "bg-[#00E676] animate-pulse" : "bg-zinc-600"}`} />
              <span className="text-zinc-400">{running ? "STREAMING · LIVE" : "STREAM PAUSED"}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mr-1">// DEMO TOOLS</span>
              <button
                type="button"
                onClick={injectRandom}
                disabled={!running || isReplay}
                data-testid="industrial-demo-inject-random"
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 hover:bg-[#FFB000]/15 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Force a +35% anomaly on a random tag for the next 3 ticks"
              >
                <Zap className="w-3 h-3" /> Inject random anomaly
              </button>
              <button
                type="button"
                onClick={saveSnapshot}
                disabled={isReplay}
                data-testid="industrial-demo-save-snapshot"
                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#FFB000] hover:text-[#FFB000] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                title="Freeze the current moment into a shareable URL"
              >
                <Camera className="w-3 h-3" /> Save snapshot
              </button>
              <button
                type="button"
                onClick={() => setRunning(r => !r)}
                disabled={isReplay}
                data-testid="industrial-demo-toggle"
                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border disabled:opacity-40 disabled:cursor-not-allowed ${
                  running
                    ? "border-zinc-700 text-zinc-400 hover:border-[#FFB000] hover:text-[#FFB000]"
                    : "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                }`}
              >
                {running ? "Pause stream" : "Resume stream"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tag table */}
      <section className="border-b border-zinc-900 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// TAG STREAM · LIVE</div>
          <div className="border border-zinc-900 overflow-x-auto" data-testid="industrial-demo-tag-table">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="bg-zinc-950 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left p-3 border-b border-zinc-900">Tag ID</th>
                  <th className="text-left p-3 border-b border-zinc-900">Description</th>
                  <th className="text-left p-3 border-b border-zinc-900">Source</th>
                  <th className="text-right p-3 border-b border-zinc-900">Live value</th>
                  <th className="text-right p-3 border-b border-zinc-900">Baseline</th>
                  <th className="text-right p-3 border-b border-zinc-900">Δ %</th>
                  <th className="text-left p-3 border-b border-zinc-900">Status</th>
                  <th className="text-left p-3 border-b border-zinc-900">Spark · 24t</th>
                  <th className="text-right p-3 border-b border-zinc-900 w-[60px]">Demo</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((t, idx) => {
                  const toneCls =
                    t.flag.tone === "high" ? "text-[#FF6B47] border-[#FF6B47]/40 bg-[#FF6B47]/5"
                      : t.flag.tone === "watch" ? "text-[#FFB000] border-[#FFB000]/40 bg-[#FFB000]/5"
                      : "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5";
                  const deltaCls = t.flag.tone === "high"
                    ? "text-[#FF6B47]"
                    : t.flag.tone === "watch" ? "text-[#FFB000]" : "text-zinc-500";
                  return (
                    <tr key={t.id} className="border-b border-zinc-900" data-testid={`industrial-demo-tag-${idx}`}>
                      <td className="p-3 font-mono text-[10px] text-zinc-400">{t.id}</td>
                      <td className="p-3 text-zinc-200">{t.label}</td>
                      <td className="p-3 font-mono text-[10px] text-zinc-500">{t.source}</td>
                      <td className="p-3 text-right font-mono text-zinc-100 tabular-nums">
                        {fmtNum(t.value, t.unit)} <span className="text-zinc-500">{t.unit}</span>
                      </td>
                      <td className="p-3 text-right font-mono text-zinc-500 tabular-nums">{fmtNum(t.baseline, t.unit)}</td>
                      <td className={`p-3 text-right font-mono font-bold tabular-nums ${deltaCls}`}>
                        {t.pct > 0 ? "+" : ""}{t.pct.toFixed(1)} %
                      </td>
                      <td className="p-3">
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${toneCls}`}>
                          {t.flag.label}
                        </span>
                      </td>
                      <td className="p-3">
                        <TagSparkline points={t.history} baseline={t.baseline} tone={t.flag.tone} />
                      </td>
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => injectAnomaly(t.id, t.pct >= 0 ? 1 : -1)}
                          disabled={!running}
                          data-testid={`industrial-demo-inject-${idx}`}
                          title="Force a +35% anomaly on this tag for the next 3 ticks"
                          className={`inline-flex items-center justify-center w-6 h-6 border transition-colors ${
                            t.injectedTicks > 0
                              ? "border-[#FF6B47] text-[#FF6B47] bg-[#FF6B47]/10 animate-pulse"
                              : "border-zinc-800 text-zinc-600 hover:border-[#FFB000] hover:text-[#FFB000] disabled:opacity-30 disabled:cursor-not-allowed"
                          }`}
                        >
                          <Zap className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Auto-brief (streams from the live tag state) */}
      <section className="border-b border-zinc-900 bg-[#FFB000]/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2 flex-wrap">
            <TrendingUp className="w-3.5 h-3.5" /> // DARA AUTO-BRIEF · STREAMING FROM LIVE TAG STATE
            {isStreaming && !briefHeld && (
              <span className="flex items-center gap-1.5 text-zinc-400 normal-case tracking-normal font-mono text-[10px]" data-testid="industrial-demo-streaming">
                <span className="w-1.5 h-1.5 bg-[#FFB000] rounded-full animate-pulse" />
                DARA is writing…
              </span>
            )}
            {briefHeld && (
              <span className="flex items-center gap-1.5 text-[#00E676] normal-case tracking-normal font-mono text-[10px]">
                <span className="w-1.5 h-1.5 bg-[#00E676] rounded-full" />
                BRIEF HELD · regeneration paused
              </span>
            )}
            <button
              type="button"
              onClick={() => setBriefTrigger(b => b + 1)}
              disabled={briefHeld || isReplay}
              data-testid="industrial-demo-refresh-brief"
              className="ml-auto font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 hover:bg-[#FFB000]/15 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              title="Regenerate the brief from current tag state"
            >
              <Sparkles className="w-3 h-3" /> Refresh brief
            </button>
            <button
              type="button"
              onClick={() => setBriefHeld(h => !h)}
              data-testid="industrial-demo-hold-brief"
              className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1 border ${
                briefHeld
                  ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10"
                  : "border-zinc-700 text-zinc-400 hover:border-[#FFB000] hover:text-[#FFB000]"
              }`}
              title="Pause the brief from regenerating so you can read it"
            >
              {briefHeld ? "Resume brief" : "Hold brief"}
            </button>
          </div>
          <p className="text-base text-zinc-100 leading-relaxed min-h-[5em]" data-testid="industrial-demo-brief">
            {displayedBrief}
            {isStreaming && !briefHeld && <span className="inline-block w-2 h-4 bg-[#FFB000] ml-1 align-middle animate-pulse" aria-hidden="true" />}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-4">
            // PRODUCTION BRIEF SHIPS DAILY AT 06:00 LOCAL · DEMO BRIEF STAYS PUT UNTIL YOU CLICK REFRESH OR INJECT AN ANOMALY
          </p>

          {/* Brief history — last 6 fully-formed briefs, oldest at bottom */}
          {briefHistory.length > 1 && (
            <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="industrial-demo-brief-history">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">// PREVIOUS BRIEFS · LAST {briefHistory.length} · MOST RECENT FIRST</div>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {briefHistory.slice(1).map((b, i) => (
                  <div key={b.id} className="text-sm text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-4" data-testid={`industrial-demo-brief-history-${i}`}>
                    <div className="font-mono text-[10px] text-zinc-600 mb-1">{b.generatedAt} AWST</div>
                    {b.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Architecture flow */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// THE ARCHITECTURE · WHAT YOU SEE ABOVE, WIRED UP FOR YOUR SITE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Four steps. <span className="text-[#FFB000]">3-4 weeks for a tier-2 site with a working historian.</span>
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="industrial-demo-architecture">
          {ARCH_STEPS.map(({ code, title, body }) => (
            <div key={code} className="bg-black p-5">
              <div className="font-heading font-black text-4xl tracking-tighter text-[#FFB000] mb-2">{code}</div>
              <div className="font-heading font-bold text-base tracking-tight mb-2">{title}</div>
              <p className="text-xs text-zinc-300 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Architecture diagram */}
        <div className="mt-8 border border-zinc-900 bg-[#0a0a0a] p-6 overflow-x-auto">
          <svg viewBox="0 0 1100 220" className="w-full min-w-[900px]" data-testid="industrial-demo-arch-svg">
            {/* Mine site box */}
            <rect x="20" y="60" width="240" height="110" rx="4" fill="#0a0a0a" stroke="#27272a" strokeWidth="1" />
            <text x="40" y="90" fill="#a1a1aa" fontSize="11" fontFamily="monospace">// MINE SITE (DMZ)</text>
            <text x="40" y="118" fill="#fafafa" fontSize="13" fontWeight="bold">Historian · SCADA · FMS</text>
            <text x="40" y="140" fill="#71717a" fontSize="10" fontFamily="monospace">PI · Aveva · Ignition · Wonderware</text>
            <text x="40" y="156" fill="#71717a" fontSize="10" fontFamily="monospace">Wenco · Modular · Sandvik</text>

            {/* Arrow 1 */}
            <line x1="260" y1="115" x2="340" y2="115" stroke="#FFB000" strokeWidth="2" />
            <polygon points="340,110 350,115 340,120" fill="#FFB000" />
            <text x="270" y="105" fill="#FFB000" fontSize="9" fontFamily="monospace">READ-ONLY</text>

            {/* DARA Connector */}
            <rect x="350" y="60" width="240" height="110" rx="4" fill="#0a0a0a" stroke="#FFB000" strokeWidth="1.5" />
            <text x="370" y="90" fill="#FFB000" fontSize="11" fontFamily="monospace">// DARA CONNECTOR</text>
            <text x="370" y="118" fill="#fafafa" fontSize="13" fontWeight="bold">Read · Encrypt · Tunnel</text>
            <text x="370" y="140" fill="#71717a" fontSize="10" fontFamily="monospace">OPC UA · Modbus · MQTT · PI</text>
            <text x="370" y="156" fill="#71717a" fontSize="10" fontFamily="monospace">No write · No PLC touch</text>

            {/* Arrow 2 */}
            <line x1="590" y1="115" x2="670" y2="115" stroke="#FFB000" strokeWidth="2" />
            <polygon points="670,110 680,115 670,120" fill="#FFB000" />
            <text x="600" y="105" fill="#FFB000" fontSize="9" fontFamily="monospace">TLS 1.3</text>

            {/* DARA Cloud */}
            <rect x="680" y="60" width="240" height="110" rx="4" fill="#0a0a0a" stroke="#27272a" strokeWidth="1" />
            <text x="700" y="90" fill="#a1a1aa" fontSize="11" fontFamily="monospace">// DARA CLOUD · AU SOVEREIGN</text>
            <text x="700" y="118" fill="#fafafa" fontSize="13" fontWeight="bold">DARA Engine · Brief</text>
            <text x="700" y="140" fill="#71717a" fontSize="10" fontFamily="monospace">Tenant-isolated · AES-256</text>
            <text x="700" y="156" fill="#71717a" fontSize="10" fontFamily="monospace">Zero training on your data</text>

            {/* Arrow 3 */}
            <line x1="920" y1="115" x2="1000" y2="115" stroke="#FFB000" strokeWidth="2" />
            <polygon points="1000,110 1010,115 1000,120" fill="#FFB000" />
            <text x="930" y="105" fill="#FFB000" fontSize="9" fontFamily="monospace">06:00 LOCAL</text>

            {/* Inbox */}
            <rect x="1010" y="60" width="80" height="110" rx="4" fill="#0a0a0a" stroke="#27272a" strokeWidth="1" />
            <text x="1020" y="90" fill="#a1a1aa" fontSize="11" fontFamily="monospace">// INBOX</text>
            <text x="1020" y="118" fill="#fafafa" fontSize="13" fontWeight="bold">Brief</text>
            <text x="1020" y="140" fill="#71717a" fontSize="10" fontFamily="monospace">PDF</text>
            <text x="1020" y="156" fill="#71717a" fontSize="10" fontFamily="monospace">Email</text>
          </svg>
        </div>
      </section>

      {/* Honest limits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// HONEST LIMITS · WHAT DARA WILL NOT DO</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          The scope is the scope. <span className="text-[#FFB000]">Read-only intelligence, not control-room replacement.</span>
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
          {HONEST_LIMITS.map((l, i) => (
            <div key={i} className="bg-black p-5 flex items-start gap-3" data-testid={`industrial-demo-limit-${i}`}>
              <AlertTriangle className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300 leading-relaxed">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Phase 25.14c — Regulator Pack (water utility only). Visually
          credible "pack of compliance artefacts your regulator wants by
          2027" — IEC 62443, NIST CSF 2.0, AS/NZS 4360 alignment. The
          actual PDF is generated server-side from a static template via
          the existing /api/public/* PDF route. */}
      {profileId === "water" && <RegulatorPackPanel siteName={profile.name} />}

      {/* CTAs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.04] p-6 sm:p-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// NEXT STEP</div>
            <h3 className="font-heading font-bold text-xl sm:text-2xl tracking-tight">Run this on your real site for 12 weeks. Free pilot.</h3>
            <p className="text-sm text-zinc-300 leading-relaxed mt-2 max-w-2xl">
              DARA absorbs the connector build, cloud, and inference costs. You provide read-only access to one historian or FMS and a 30-minute superintendent intro. First reconciled brief in 4 weeks.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/industrial/connector"
              data-testid="industrial-demo-cta-pilot"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-5 py-3 hover:opacity-90"
            >
              Start a 12-week pilot <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="/api/public/industrial-connector-brief"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="industrial-demo-cta-brief"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Connector brief PDF
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between flex-wrap gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · INDUSTRIAL CONNECTOR · LIVE DEMO · v1</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">All data simulated client-side · no network calls</span>
        </div>
      </footer>
    </div>
  );
}

function StatusCell({ label, value, accent, highlight }) {
  return (
    <div className={`p-4 ${highlight ? "bg-[#FF6B47]/5" : "bg-black"}`}>
      <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${accent ? "text-[#FFB000]" : "text-zinc-500"}`}>
        // {label}
      </div>
      <div className={`font-heading font-bold tracking-tight ${highlight ? "text-[#FF6B47] text-lg" : "text-white text-base"} truncate`}>
        {value}
      </div>
    </div>
  );
}

function TagSparkline({ points, baseline, tone }) {
  if (!points || points.length < 2) return <span className="text-zinc-700 font-mono text-[10px]">—</span>;
  const min = Math.min(...points, baseline);
  const max = Math.max(...points, baseline);
  const range = Math.max(1, max - min);
  const W = 110;
  const H = 26;
  const stepX = W / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = H - ((p - min) / range) * (H - 4) - 2;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const baselineY = H - ((baseline - min) / range) * (H - 4) - 2;
  const strokeColour =
    tone === "high" ? "#FF6B47" : tone === "watch" ? "#FFB000" : "#00E676";
  return (
    <svg width={W} height={H} className="block">
      <line x1="0" y1={baselineY} x2={W} y2={baselineY} stroke="#3f3f46" strokeWidth="1" strokeDasharray="2 2" />
      <path d={path} stroke={strokeColour} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

// Phase 25.14c — Regulator Pack Panel (water utility).
// Closes the credibility gap on /industries/water "Regulator-Ready Reporting":
// presents a visually concrete pack of artefacts auditors and regulators ask
// for (IEC 62443 alignment, NIST CSF 2.0 control mapping, AS/NZS 4360 risk
// register). Each row is illustrative, not a substitute for a real auditor —
// the page itself says so. The "Download Pack" CTA opens a printable HTML
// version on a new tab so it can be screenshotted into a proposal.
const REGULATOR_PACK_ROWS = [
  { code: "IEC 62443-3-3", label: "System Security Requirements (SR)", coverage: "12 / 12", notes: "Read-only client. No PLC actuation. SR 1, 2, 3, 5, 7 fully aligned." },
  { code: "IEC 62443-4-1", label: "Secure product development lifecycle", coverage: "Aligned", notes: "DARA Engine versioned releases, SBOM published per release." },
  { code: "NIST CSF 2.0", label: "Identify · Protect · Detect · Respond", coverage: "ID.AM, PR.AC, DE.CM, DE.AE", notes: "Anomaly detection (DE.AE) and continuous monitoring (DE.CM) covered by the morning brief workflow." },
  { code: "AS/NZS 4360", label: "Risk register — water-asset class", coverage: "Live", notes: "Each anomaly logged to the tenant's risk register with consequence × likelihood × residual rating." },
  { code: "ACSC ISM (AU)", label: "OT control 1735 · Read-only telemetry", coverage: "Compliant", notes: "Egress-only TLS 1.3 from DMZ. No inbound. AU-sovereign storage." },
  { code: "WSAA G2", label: "Water Services Association cyber guideline", coverage: "Roadmap", notes: "Mapping to be finalised in pilot months 2-3 with the utility's CISO." },
];

function RegulatorPackPanel({ siteName }) {
  const now = new Date();
  const generated = now.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  const buildId = `RP-${now.toISOString().slice(0, 10).replace(/-/g, "")}-${Math.floor(Math.random() * 9000 + 1000)}`;
  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-zinc-900"
      data-testid="regulator-pack-panel"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            // REGULATOR PACK · GENERATED FROM THIS DEMO SESSION
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            Audit-defensible by design. <span className="text-[#FFB000]">Every anomaly tagged to a framework.</span>
          </h2>
        </div>
        <div className="font-mono text-[10px] text-zinc-600 leading-snug text-right">
          <div>Generated · {generated}</div>
          <div>Pack ID · {buildId}</div>
          <div className="text-zinc-700">Site · {siteName}</div>
        </div>
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl mb-6">
        DARA isn&apos;t a compliance product, but every reading it takes is auditable.
        Each anomaly is automatically tagged to the control framework your regulator
        cares about, and reconciled into a downloadable pack your CISO can hand
        straight to the auditor.
      </p>

      <div className="border border-zinc-900 bg-zinc-950/40 overflow-x-auto">
        <table className="w-full font-mono text-[11px]">
          <thead className="bg-zinc-950">
            <tr className="text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="text-left px-4 py-2.5 border-b border-zinc-900">Framework / Standard</th>
              <th className="text-left px-4 py-2.5 border-b border-zinc-900">Control</th>
              <th className="text-left px-4 py-2.5 border-b border-zinc-900">Coverage</th>
              <th className="text-left px-4 py-2.5 border-b border-zinc-900 hidden md:table-cell">Mapping notes</th>
            </tr>
          </thead>
          <tbody>
            {REGULATOR_PACK_ROWS.map((r, i) => (
              <tr key={r.code} className={i % 2 ? "bg-black" : "bg-zinc-950/40"} data-testid={`regulator-pack-row-${i}`}>
                <td className="px-4 py-3 text-white">{r.code}</td>
                <td className="px-4 py-3 text-zinc-300">{r.label}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 ${
                      r.coverage === "Roadmap"
                        ? "text-[#FFB000]"
                        : "text-[#00E676]"
                    }`}
                  >
                    {r.coverage === "Roadmap"
                      ? <AlertTriangle className="w-3 h-3" />
                      : <CheckCircle2 className="w-3 h-3" />}
                    {r.coverage}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell leading-snug">{r.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <a
          href="/regulator-pack-preview"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="regulator-pack-download"
          className="inline-flex items-center gap-2 border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] font-mono text-[11px] uppercase tracking-widest px-4 py-2.5"
        >
          <Download className="w-3.5 h-3.5" />
          View pack preview
        </a>
        <Link
          to="/industrial/connector"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Talk to a CISO about the real mapping
        </Link>
        <span className="font-mono text-[10px] text-zinc-700 ml-auto leading-snug max-w-md text-right">
          Illustrative pack. Final framework mapping finalised in pilot weeks 2-3 with the utility&apos;s CISO and named auditor.
        </span>
      </div>
    </section>
  );
}

